import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeftIcon, ArrowRightIcon, KeyRoundIcon, Loader2Icon, MailIcon, SmartphoneIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage, isMfaPending } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import type { CodeRequestResponse, LoginMethods, MfaPendingResponse, SessionResponse } from '@/lib/types';
import { MfaStep } from './MfaStep';
import { SsoButtons } from './SsoButtons';

const passwordSchema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});
type PasswordValues = z.infer<typeof passwordSchema>;

const identifierSchema = z.object({ identifier: z.string().trim().min(3, 'Enter your email address or mobile number') });
type IdentifierValues = z.infer<typeof identifierSchema>;

const codeSchema = z.object({ code: z.string().trim().regex(/^\d{3}\s?\d{3}$/, 'Enter the 6-digit code') });
type CodeValues = z.infer<typeof codeSchema>;

/** Opt-in convenience: only the email address, only on this device, only after a successful sign-in. */
const REMEMBER_KEY = 'crewline-remembered-email';
function readRememberedEmail(): string {
  try {
    return localStorage.getItem(REMEMBER_KEY) ?? '';
  } catch {
    return '';
  }
}
function writeRememberedEmail(email: string | null): void {
  try {
    if (email) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch {
    /* ignore */
  }
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

type Mode = 'password' | 'code';

const SIGNED_OUT_COPY: Record<'idle' | 'expired' | 'tenant', string> = {
  idle: 'You were signed out after a period of inactivity. Sign in again to continue.',
  expired: 'Your session ended. Sign in again to continue.',
  tenant: 'You were signed in to a different organization. Sign in here to continue.',
};

export function LoginPage() {
  const { branding } = useBranding();
  const { signedOutReason, adoptSession } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const done = useAfterLogin();
  const methods: LoginMethods = branding.loginMethods ?? { password: true, emailCode: false, smsCode: false, sso: { mode: 'off', providers: [] } };
  const ssoProviders = methods.sso?.mode === 'off' ? [] : (methods.sso?.providers ?? []);
  const ssoOnly = methods.sso?.mode === 'required' && ssoProviders.length > 0;
  const codeAvailable = methods.emailCode || methods.smsCode;
  const [mode, setMode] = useState<Mode>(methods.password ? 'password' : 'code');
  const [pending, setPending] = useState<MfaPendingResponse | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const ssoError = params.get('ssoError');
  const returnTo = params.get('return') ?? (location.state as { from?: string } | null)?.from ?? null;
  const signIn = branding.signIn;

  // After a single sign-on round-trip that still needs a second step, the API left a short-lived ticket cookie.
  useEffect(() => {
    if (params.get('mfa') !== 'pending') return;
    api.auth.mfa
      .pending()
      .then(setPending)
      .catch((err) => setPendingError(errorMessage(err, 'Your sign-in expired. Start again.')))
      .finally(() => {
        const next = new URLSearchParams(params);
        next.delete('mfa');
        setParams(next, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = async (session: SessionResponse) => {
    await adoptSession(session);
    done();
  };

  const description = pending
    ? pending.mfa.stage === 'verify'
      ? 'One more step: confirm it’s really you.'
      : 'Set up two-step verification to finish signing in.'
    : ssoOnly
      ? 'Sign in with the work account your organization uses.'
      : !methods.password
        ? codeDescription(methods)
        : mode === 'password'
          ? 'Use the work email address and password for your account.'
          : codeDescription(methods);

  return (
    <AuthLayout
      eyebrow={pending ? 'Two-step verification' : greeting()}
      title={pending ? (pending.mfa.stage === 'verify' ? 'Verify it’s you' : 'One more step') : signIn?.title?.trim() || 'Sign in'}
      description={description}
      footer={
        signIn?.footerNote?.trim() ? (
          <span>{signIn.footerNote}</span>
        ) : (
          <span>
            New to <span className="text-foreground font-medium">{branding.appName}</span>? Access is by invitation only. Ask your organization administrator to invite you.
          </span>
        )
      }
    >
      {!pending && (signedOutReason === 'idle' || signedOutReason === 'expired' || signedOutReason === 'tenant') ? (
        <div role="status" className="bg-muted/60 text-muted-foreground mb-5 border p-3 text-sm leading-relaxed">
          {SIGNED_OUT_COPY[signedOutReason]}
        </div>
      ) : null}
      {ssoError && !pending ? (
        <div role="alert" className="border-destructive/40 bg-destructive/5 text-destructive mb-5 border p-3 text-sm leading-relaxed">
          {ssoError}
        </div>
      ) : null}
      {pendingError ? <FormError message={pendingError} className="mb-5" /> : null}

      {pending ? (
        <MfaStep pending={pending} onSession={finish} onBack={() => setPending(null)} />
      ) : (
        <>
          {ssoProviders.length > 0 ? <SsoButtons providers={ssoProviders} returnTo={returnTo} /> : null}
          {!ssoOnly && ssoProviders.length > 0 ? (
            <div className="text-muted-foreground relative my-5 text-center text-[11px] tracking-[0.18em] uppercase">
              <span className="bg-background relative z-10 px-2">or</span>
              <span className="absolute inset-x-0 top-1/2 border-t" aria-hidden />
            </div>
          ) : null}
          {!ssoOnly ? (mode === 'password' ? <PasswordForm onMfa={setPending} /> : <CodeForm methods={methods} onMfa={setPending} />) : null}

          {!ssoOnly && methods.password && codeAvailable ? (
            <div className="mt-6 border-t pt-5 text-center text-sm">
              {mode === 'password' ? (
                <button type="button" onClick={() => setMode('code')} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 underline-offset-4 hover:underline">
                  <KeyRoundIcon className="size-3.5" aria-hidden /> Sign in with a one-time code instead
                </button>
              ) : (
                <button type="button" onClick={() => setMode('password')} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 underline-offset-4 hover:underline">
                  <ArrowLeftIcon className="size-3.5" aria-hidden /> Sign in with a password instead
                </button>
              )}
            </div>
          ) : null}
        </>
      )}
    </AuthLayout>
  );
}

function codeDescription(m: { emailCode: boolean; smsCode: boolean }): string {
  if (m.emailCode && m.smsCode) return 'Enter your work email address or mobile number and we will send you a one-time sign-in code.';
  if (m.smsCode) return 'Enter your mobile number and we will text you a one-time sign-in code.';
  return 'Enter your work email address and we will email you a one-time sign-in code.';
}

function useAfterLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  return () => navigate(from && from !== '/login' ? from : '/jobs', { replace: true });
}

// ---------------------------------------------------------------- password

function PasswordForm({ onMfa }: { onMfa: (pending: MfaPendingResponse) => void }) {
  const { login } = useAuth();
  const done = useAfterLogin();
  const [error, setError] = useState<string | null>(null);
  const [remembered] = useState(readRememberedEmail);
  const [remember, setRemember] = useState(() => remembered.length > 0);
  const form = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema), defaultValues: { email: remembered, password: '' } });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    const email = values.email.trim();
    try {
      const result = await login(email, values.password);
      writeRememberedEmail(remember ? email : null);
      if (result.kind === 'mfa') {
        onMfa(result.pending);
        return;
      }
      done();
    } catch (err) {
      setError(errorMessage(err, 'Could not sign you in'));
    }
  });

  return (
    <form onSubmit={onSubmit} className="grid gap-5" noValidate>
      <FormError message={error} />
      <Field label="Email" htmlFor="email" error={errors.email?.message}>
        <Input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus={!remembered}
          placeholder="you@company.com"
          aria-invalid={!!errors.email}
          {...form.register('email')}
        />
      </Field>
      <Field
        label="Password"
        htmlFor="password"
        error={errors.password?.message}
        labelAction={
          <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline">
            Forgot password?
          </Link>
        }
      >
        <PasswordInput id="password" autoComplete="current-password" autoFocus={!!remembered} placeholder="Your password" aria-invalid={!!errors.password} {...form.register('password')} />
      </Field>

      <div className="flex items-center gap-2.5">
        <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(v === true)} />
        <Label htmlFor="remember" className="text-muted-foreground cursor-pointer font-normal">
          Remember my email on this device
        </Label>
      </div>

      <Button type="submit" size="lg" className="group mt-1 w-full" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Loader2Icon className="animate-spin" aria-hidden /> Signing in…
          </>
        ) : (
          <>
            Sign in
            <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
          </>
        )}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------- one-time code

function CodeForm({ methods, onMfa }: { methods: { emailCode: boolean; smsCode: boolean }; onMfa: (pending: MfaPendingResponse) => void }) {
  const { adoptSession } = useAuth();
  const done = useAfterLogin();
  const [error, setError] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState<string>('');
  const [sent, setSent] = useState<CodeRequestResponse | null>(null);
  const [resendIn, setResendIn] = useState(0);

  const idForm = useForm<IdentifierValues>({ resolver: zodResolver(identifierSchema), defaultValues: { identifier: '' } });
  const codeForm = useForm<CodeValues>({ resolver: zodResolver(codeSchema), defaultValues: { code: '' } });

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const send = async (value: string) => {
    setError(null);
    try {
      const res = await api.auth.requestCode(value);
      setIdentifier(value);
      setSent(res);
      setResendIn(res.resendAfterSeconds);
      codeForm.reset({ code: '' });
    } catch (err) {
      setError(errorMessage(err, 'Could not send a code'));
    }
  };

  const onRequest = idForm.handleSubmit((v) => send(v.identifier.trim()));

  const onVerify = codeForm.handleSubmit(async (v) => {
    setError(null);
    try {
      const outcome = await api.auth.verifyCode(identifier, v.code.replace(/\s+/g, ''));
      if (isMfaPending(outcome)) {
        onMfa(outcome);
        return;
      }
      await adoptSession(outcome);
      done();
    } catch (err) {
      setError(errorMessage(err, 'That code did not work'));
    }
  });

  const placeholder = methods.emailCode && methods.smsCode ? 'you@company.com or (407) 555-0123' : methods.smsCode ? '(407) 555-0123' : 'you@company.com';
  const inputMode = methods.emailCode && methods.smsCode ? 'text' : methods.smsCode ? 'tel' : 'email';

  if (!sent) {
    return (
      <form onSubmit={onRequest} className="grid gap-5" noValidate>
        <FormError message={error} />
        <Field label={methods.emailCode && methods.smsCode ? 'Email or mobile number' : methods.smsCode ? 'Mobile number' : 'Email'} htmlFor="identifier" error={idForm.formState.errors.identifier?.message}>
          <Input
            id="identifier"
            inputMode={inputMode}
            autoComplete={methods.smsCode && !methods.emailCode ? 'tel' : 'username'}
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            placeholder={placeholder}
            aria-invalid={!!idForm.formState.errors.identifier}
            {...idForm.register('identifier')}
          />
        </Field>
        <Button type="submit" size="lg" className="group mt-1 w-full" disabled={idForm.formState.isSubmitting}>
          {idForm.formState.isSubmitting ? (
            <>
              <Loader2Icon className="animate-spin" aria-hidden /> Sending code…
            </>
          ) : (
            <>
              Send me a code
              <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={onVerify} className="grid gap-5" noValidate>
      <div className="bg-muted/50 text-muted-foreground flex items-start gap-3 border p-3 text-sm">
        {sent.channel === 'sms' ? <SmartphoneIcon className="mt-0.5 size-4 shrink-0" aria-hidden /> : <MailIcon className="mt-0.5 size-4 shrink-0" aria-hidden />}
        <span>
          We sent a 6-digit code to <span className="text-foreground font-medium">{sent.destination}</span>. It expires in {Math.round(sent.expiresInSeconds / 60)} minutes.
        </span>
      </div>
      <FormError message={error} />
      <Field label="Sign-in code" htmlFor="code" error={codeForm.formState.errors.code?.message}>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9 ]*"
          maxLength={7}
          autoFocus
          placeholder="123 456"
          className="text-center text-xl tracking-[0.4em]"
          aria-invalid={!!codeForm.formState.errors.code}
          {...codeForm.register('code')}
        />
      </Field>
      <Button type="submit" size="lg" className="group mt-1 w-full" disabled={codeForm.formState.isSubmitting}>
        {codeForm.formState.isSubmitting ? (
          <>
            <Loader2Icon className="animate-spin" aria-hidden /> Verifying…
          </>
        ) : (
          <>
            Sign in
            <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
          </>
        )}
      </Button>
      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <button type="button" onClick={() => void send(identifier)} disabled={resendIn > 0} className="hover:text-foreground underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60">
          {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
        </button>
        <button
          type="button"
          onClick={() => {
            setSent(null);
            setError(null);
          }}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          Use a different {methods.emailCode && methods.smsCode ? 'email or number' : methods.smsCode ? 'number' : 'email'}
        </button>
      </div>
    </form>
  );
}
